import React, { useState, useEffect } from 'react';
import { useOverlayLayers } from '../../hooks/useOverlayLayers';
import { usePublicWebSocket } from '../../hooks/usePublicWebSocket';
import { useLogoStyle } from '../../hooks/useLogoStyle';
import ScoreboardTop from './ScoreboardTop';
import EventPopupLayout from './EventPopupLayout';
import VARReviewOverlay from './VARReviewOverlay';
import FormationsOverlay from './FormationsOverlay';

/**
 * All Layers Layout
 * Renders all overlay layers - ALWAYS VISIBLE in this mode
 * Visibility is controlled externally via WebSocket when needed
 * 
 * Usage: /watch/:matchId?layout=all-layers
 * This layout is intended for OBS Browser Source
 */
const getResolutionLabel = (w, h) => {
  if (w === 1280 && h === 720) return 'HD';
  if (w === 1920 && h === 1080) return 'Full HD';
  if (w === 2560 && h === 1440) return '2K';
  if (w === 3840 && h === 2160) return '4K';
  return 'Custom';
};

export const AllLayersLayout = ({ matchId }) => {
  // Single WebSocket hook - maintains stable connection
  const wsData = usePublicWebSocket(matchId);
  const { layers } = useOverlayLayers(matchId);
  const { broadcasterLogo, broadcasterLogoPosition, overlayResolution } = useLogoStyle();
  const [showResLabel, setShowResLabel] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowResLabel(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  console.log('[AllLayersLayout] Render - layers:', layers);

  const shouldShowLayer = (layerId) => {
    const result = layers[layerId] === true;
    console.log(`[AllLayersLayout] shouldShowLayer(${layerId}):`, result, 'value:', layers[layerId]);
    return result;
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{
        width: overlayResolution.width + 'px',
        height: overlayResolution.height + 'px'
      }}
    >
      {/* Resolution indicator - visible for 3 seconds on load */}
      {showResLabel && (
        <div className="absolute top-2 right-2 z-[60] bg-black/60 text-white text-xs px-2 py-1 rounded font-mono pointer-events-none transition-opacity duration-500"
          style={{ opacity: showResLabel ? 0.8 : 0 }}
        >
          {overlayResolution.width}x{overlayResolution.height} ({getResolutionLabel(overlayResolution.width, overlayResolution.height)})
        </div>
      )}

      {/* Debug overlay - only visible with ?debug=true */}
      {new URLSearchParams(window.location.search).get('debug') === 'true' && (
        <div className="absolute top-4 right-4 bg-black/80 text-white p-2 text-xs rounded z-50">
          <div>Mode: All Layers</div>
          <div>Match: {matchId}</div>
          <div>Scoreboard: {shouldShowLayer('scoreboard-top') ? '✓' : '✗'}</div>
          <div>Logo: {shouldShowLayer('broadcaster-logo') ? '✓' : '✗'}</div>
          <div>Formations: {shouldShowLayer('formations') ? '✓' : '✗'}</div>
          <div>Layers state: {JSON.stringify(layers)}</div>
        </div>
      )}

      {/* Scoreboard Top - Controlled by WebSocket toggle */}
      {shouldShowLayer('scoreboard-top') && (
        <div className="absolute top-0 left-0 right-0 z-30">
          <ScoreboardTop wsData={wsData} matchId={matchId} />
        </div>
      )}

      {/* Broadcaster Logo - Independent layer, controlled by operator */}
      {shouldShowLayer('broadcaster-logo') && broadcasterLogo && (
        <div className={`absolute z-10 ${
          broadcasterLogoPosition === 'top-left' ? 'top-4 left-4' :
          broadcasterLogoPosition === 'top-right' ? 'top-4 right-4' :
          broadcasterLogoPosition === 'bottom-left' ? 'bottom-4 left-4' :
          'bottom-4 right-4'
        }`}>
          <img
            src={broadcasterLogo}
            alt="Broadcaster Logo"
            className="h-12 object-contain opacity-80"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Formations Overlay - Controlled by operator toggle */}
      {shouldShowLayer('formations') && (
        <div className="absolute inset-0 z-[15]">
          <FormationsOverlay wsData={wsData} matchId={matchId} />
        </div>
      )}

      {/* Event Popup - Always rendered, controls its own visibility based on events */}
      <div className="absolute inset-0 z-20">
        <EventPopupLayout wsData={wsData} />
      </div>

      {/* VAR Review Overlay - Always rendered, controls its own visibility */}
      <div className="absolute inset-0 z-[45]">
        <VARReviewOverlay wsData={wsData} />
      </div>
    </div>
  );
};

export default AllLayersLayout;
